// skipcq: JS-W1028
import MenuIcon from '@mui/icons-material/Menu';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

import { logout } from '../../utils/firebase';
import { elevatedPaperSurface } from '../../utils/vintageSurfaces';
import BrewhouseActiveLocationBar from './BrewhouseActiveLocationBar';
import BrewhouseBackLink from './BrewhouseBackLink';
import BrewhouseBreadcrumbs from './BrewhouseBreadcrumbs';
import { useBrewhousePageHeaderState } from './BrewhousePageHeaderContext';

/** Matches BrewhousePortalLayout drawer width. */
export const BREWHOUSE_PORTAL_DRAWER_WIDTH = 240;

/**
 * Consolidated brewhouse portal header: nav controls, breadcrumbs, title, and logout.
 * Rendered once by {@link BrewhousePortalLayout}; pages register content via context.
 *
 * @param {object} props
 * @param {() => void} props.onDrawerToggle
 */
function BrewhousePageHeader({ onDrawerToggle }) {
  const theme = useTheme();
  const { breadcrumbs, backLink, title, titleTestId } = useBrewhousePageHeaderState();

  const hasBreadcrumbs = breadcrumbs.length > 0;
  const hasBackLink = backLink !== null && typeof backLink.to === 'string';
  const hasTitle = typeof title === 'string' && title.length > 0;
  const showNavColumn = hasBackLink || hasBreadcrumbs;

  const titleTypographySx = {
    fontFamily: 'Lora, Georgia, serif',
    fontWeight: 600,
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar,
        borderBottom: 1,
        borderColor: 'divider',
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1, sm: 1 },
        ...elevatedPaperSurface(theme.palette.background.default),
      }}
    >
      <Box
        sx={{
          display: 'grid',
          alignItems: 'start',
          columnGap: { xs: 0.75, sm: 1 },
          rowGap: { xs: 0.75, md: 0 },
          gridTemplateColumns: {
            xs: 'auto 1fr auto',
            md: 'auto auto 1fr auto',
          },
          gridTemplateAreas: hasTitle
            ? {
                xs: `"menu nav logout"
                     "title title title"`,
                md: `"menu nav title logout"`,
              }
            : {
                xs: `"menu nav logout"`,
                md: `"menu nav . logout"`,
              },
        }}
      >
        <IconButton
          color="inherit"
          aria-label="Open navigation menu"
          edge="start"
          onClick={onDrawerToggle}
          sx={{
            display: { sm: 'none' },
            gridArea: 'menu',
            flexShrink: 0,
            ml: -0.5,
            mt: 0.25,
          }}
        >
          <MenuIcon />
        </IconButton>

        {showNavColumn ? (
          <Box
            sx={{
              gridArea: 'nav',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0.75,
              minWidth: 0,
            }}
          >
            {hasBackLink && (
              <BrewhouseBackLink
                to={backLink.to}
                label={backLink.label}
                compact
                sx={{ mb: 0 }}
              />
            )}
            {hasBreadcrumbs && (
              <BrewhouseBreadcrumbs segments={breadcrumbs} sx={{ mb: 0 }} />
            )}
          </Box>
        ) : (
          <Box sx={{ gridArea: 'nav', display: { md: 'none' } }} />
        )}

        {hasTitle && (
          <Typography
            variant="h6"
            component="h1"
            data-testid={titleTestId || undefined}
            sx={{
              ...titleTypographySx,
              gridArea: 'title',
              minWidth: 0,
              textAlign: 'center',
              pt: { md: 0.25 },
              whiteSpace: { xs: 'normal', md: 'nowrap' },
              overflow: { md: 'hidden' },
              textOverflow: { md: 'ellipsis' },
            }}
          >
            {title}
          </Typography>
        )}

        <Button
          type="button"
          color="inherit"
          onClick={logout}
          sx={{
            gridArea: 'logout',
            justifySelf: 'end',
            textTransform: 'none',
          }}
        >
          Logout
        </Button>
      </Box>
      <BrewhouseActiveLocationBar />
    </Box>
  );
}

BrewhousePageHeader.propTypes = {
  onDrawerToggle: PropTypes.func.isRequired,
};

export default BrewhousePageHeader;
